import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ChevronLeft, ChevronRight, BadgeCheck, Users, Trophy, Gem, Crown, Link2, Mail, Instagram, Youtube, Facebook, MessageCircle, Send, Globe } from 'lucide-react';
import api from '../../services/api';
import SafeImage from '../../components/SafeImage';

const SOCIALS = [
  {
    key: 'instagram', label: 'Instagram', icon: Instagram,
    cta: 'Follow', gradient: 'linear-gradient(135deg, #004D61, #006B85 52%, #004D61 100%)',
    handlePrefix: '@', subLabel: (h) => (h ? handleFromUrl(h, 'instagram.com') : ''),
    ctaColor: '#006B85', border: 'rgba(0,107,133,0.6)',
  },
  {
    key: 'youtube', label: 'YouTube', icon: Youtube,
    cta: 'Subscribe', gradient: '#FF0000', handlePrefix: '',
    subLabel: (h) => '',
    ctaColor: '#EF4444', border: 'rgba(239,68,68,0.6)',
  },
  {
    key: 'tiktok', label: 'TikTok', icon: null /* we'll render text logo */,
    cta: 'Follow', gradient: '#000000', handlePrefix: '@',
    subLabel: (h) => (h ? handleFromUrl(h, 'tiktok.com') : ''),
    ctaColor: '#004D61', border: 'rgba(0,77,97,0.6)',
  },
  {
    key: 'facebook', label: 'Facebook', icon: Facebook,
    cta: 'Like', gradient: '#1877F2', handlePrefix: '',
    subLabel: (h) => '',
    ctaColor: '#3B82F6', border: 'rgba(59,130,246,0.6)',
  },
  {
    key: 'whatsapp', label: 'WhatsApp', icon: MessageCircle,
    cta: 'Chat', gradient: '#25D366', handlePrefix: '',
    subLabel: (h) => (h ? String(h).replace(/^https?:\/\//, '') : ''),
    ctaColor: '#22C55E', border: 'rgba(34,197,94,0.6)',
  },
  {
    key: 'discord', label: 'Discord', icon: MessageCircle,
    cta: 'Join', gradient: '#5865F2', handlePrefix: '',
    subLabel: () => 'Join our community',
    ctaColor: '#006B85', border: 'rgba(0,107,133,0.6)',
  },
  {
    key: 'telegram', label: 'Telegram', icon: Send,
    cta: 'Join', gradient: '#26A5E4', handlePrefix: '@',
    subLabel: (h) => (h ? handleFromUrl(h, 't.me') : ''),
    ctaColor: '#06B6D4', border: 'rgba(6,182,212,0.6)',
  },
  {
    key: 'website', label: 'Website', icon: Globe,
    cta: 'Visit', gradient: 'linear-gradient(135deg, #004D61, #004D61)', handlePrefix: '',
    subLabel: () => '',
    ctaColor: '#004D61', border: 'rgba(0,77,97,0.6)',
  },
  {
    key: 'support_email', label: 'Email', icon: Mail,
    cta: 'Email', gradient: 'linear-gradient(135deg, #004D61, #004D61)', handlePrefix: '',
    subLabel: (h) => h || '',
    ctaColor: '#004D61', border: 'rgba(0,77,97,0.6)',
  },
];

function handleFromUrl(url, domain) {
  try {
    const m = url.match(new RegExp(`${domain.replace(/\./g, '\\.')}/@?([A-Za-z0-9_\\.\\-]+)`));
    return m ? `@${m[1]}` : '';
  } catch { return ''; }
}

function firstUrl(value) {
  const raw = String(value || '').trim();
  const match = raw.match(/https?:\/\/[^\s]+/i);
  return match ? match[0].replace(/[),.;]+$/, '') : '';
}

function socialHref(key, value) {
  const raw = String(value || '').trim();
  if (!raw) return '';
  const extracted = firstUrl(raw);
  if (extracted) return extracted;
  if (key === 'support_email') return raw.includes('@') ? `mailto:${raw.replace(/^mailto:/i, '')}` : '';
  if (/^https?:\/\//i.test(raw)) return raw;
  if (key === 'telegram') return `https://t.me/${raw.replace(/^@/, '').replace(/^t\.me\//i, '')}`;
  if (key === 'whatsapp') {
    if (/^(wa\.me|api\.whatsapp\.com|chat\.whatsapp\.com|whatsapp\.com)\//i.test(raw)) return `https://${raw}`;
    const digits = raw.replace(/[^\d+]/g, '').replace(/^\+/, '');
    return digits ? `https://wa.me/${digits}` : '';
  }
  return `https://${raw}`;
}

function socialValue(branding, key) {
  const socials = branding?.socials || {};
  const support = branding?.support || {};
  const aliases = {
    facebook: ['facebook', 'facebook_url'],
    whatsapp: ['whatsapp', 'whatsapp_url'],
    discord: ['discord', 'discord_url'],
    telegram: ['telegram', 'telegram_url'],
    support_email: ['support_email', 'email'],
  };
  const keys = aliases[key] || [key];
  for (const candidate of keys) {
    const value = socials[candidate] || support[candidate];
    if (value) return value;
  }
  return '';
}

export default function BrandProfilePage() {
  const navigate = useNavigate();
  const { tenantSlug } = useParams();
  const homePath = tenantSlug ? `/${tenantSlug}/app` : '/app';
  const [b, setB] = useState(null);

  useEffect(() => {
    api.get('/api/branding').then(r => setB(r.data || {})).catch(() => setB({}));
  }, []);

  if (!b) return <div className="player-app min-h-screen flex items-center justify-center text-white/70">Loading...</div>;

  const displayName = b.brand_name || 'Mystic Reels';
  const socialVisibility = b.social_visibility || {};
  const socialLinks = Object.fromEntries(SOCIALS.map((social) => [social.key, socialValue(b, social.key)]));
  const visibleSocials = SOCIALS.filter((social) => socialVisibility[social.key] !== false && socialLinks[social.key]);
  const supportEmail = b.socials?.support_email || b.support?.support_email || b.support?.email;

  return (
    <div className="player-app min-h-screen pb-12" data-testid="brand-profile">
      {/* Header with back */}
      <div
        className="sticky top-0 z-30 px-4 sm:px-6 py-4 flex items-center justify-between"
        style={{ background: '#050B1C', borderBottom: '1px solid rgba(255,217,61,0.45)' }}
      >
        <button
          onClick={() => navigate(homePath)}
          className="w-10 h-10 rounded-full flex items-center justify-center active:scale-95"
          style={{ background: 'rgba(0,107,133,0.15)', border: '1px solid rgba(0,107,133,0.45)', color: '#006B85' }}
          aria-label="Back"
          data-testid="brand-profile-back"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
        <h1 className="display font-extrabold text-lg text-white truncate max-w-[60%]">{displayName}</h1>
        <span className="w-10" aria-hidden />
      </div>

      <div className="max-w-2xl mx-auto px-4 sm:px-6 pt-8">
        {/* Logo hero */}
        <div className="flex flex-col items-center text-center">
          <div
            className="w-44 h-44 sm:w-48 sm:h-48 rounded-full flex items-center justify-center overflow-hidden mb-5 border"
            style={{
              background: 'rgba(255,255,255,0.03)',
              borderColor: 'rgba(255,255,255,0.14)',
              boxShadow: '0 18px 50px rgba(0,0,0,0.48)',
            }}
          >
            {b.logo_url
              ? <SafeImage src={b.logo_url} alt={displayName} className="w-full h-full object-cover" data-testid="brand-profile-logo" loading="eager" fallback={<Crown className="w-24 h-24 text-white/75" />} />
              : <Crown className="w-24 h-24 text-white/75" />}
          </div>
          <h2 className="display font-extrabold text-3xl sm:text-4xl tracking-wide text-white uppercase">{displayName}</h2>
          {b.verified && (
            <div
              className="inline-flex items-center gap-1.5 mt-3 px-3 py-1 rounded-full"
              style={{ background: 'rgba(59,130,246,0.15)', border: '1px solid rgba(59,130,246,0.5)' }}
              data-testid="brand-profile-verified"
            >
              <BadgeCheck className="w-4 h-4 text-blue-400" />
              <span className="text-xs font-bold text-blue-300">Official Brand</span>
            </div>
          )}
          {b.description && (
            <p className="text-sm text-white/70 mt-4 leading-relaxed max-w-lg" data-testid="brand-profile-description">
              {b.description}
            </p>
          )}
        </div>

        {/* Stats */}
        {(b.stats?.followers || b.stats?.games || b.stats?.rewards_claimed) && (
          <div
            className="mt-8 rounded-2xl p-5 grid grid-cols-3 gap-2 sm:gap-4"
            style={{ background: 'rgba(0,107,133,0.08)', border: '1px solid rgba(0,107,133,0.30)' }}
            data-testid="brand-profile-stats"
          >
            <Stat icon={Users} color="#006B85" value={b.stats?.followers} label="Followers" />
            <Stat icon={Trophy} color="#FFD93D" value={b.stats?.games} label="Games" />
            <Stat icon={Gem} color="#004D61" value={b.stats?.rewards_claimed} label="Rewards Claimed" />
          </div>
        )}

        {/* Socials */}
        <div className="mt-8">
          <h3 className="flex items-center gap-2 text-white/80 font-bold mb-3">
            <Link2 className="w-4 h-4" /> Connect With Us
          </h3>
          <div className="space-y-2">
            {visibleSocials.map((social) => (
              <SocialRow key={social.key} def={social} url={socialLinks[social.key]} brand={displayName} />
            ))}
            {visibleSocials.length === 0 && (
              <p className="text-sm text-white/40 text-center py-4">No social links configured yet.</p>
            )}
          </div>
        </div>

        {/* Need help */}
        {b.support?.enabled !== false && supportEmail && (
          <a
            href={`mailto:${supportEmail}`}
            className="mt-6 flex items-center gap-3 rounded-2xl p-4 transition active:scale-[0.99] hover:brightness-110"
            style={{ background: 'rgba(0,107,133,0.15)', border: '1px solid rgba(0,107,133,0.45)' }}
            data-testid="brand-profile-support"
          >
            <span
              className="shrink-0 w-11 h-11 rounded-xl flex items-center justify-center"
              style={{ background: 'linear-gradient(135deg, #006B85, #004D61)' }}
            >
              <Mail className="w-5 h-5 text-white" />
            </span>
            <div className="flex-1">
              <p className="font-bold text-white">Need Help?</p>
              <p className="text-xs text-white/70">Our support team is here 24/7</p>
            </div>
            <span
              className="shrink-0 display text-xs font-bold px-3 py-1.5 rounded-full"
              style={{ background: 'rgba(0,107,133,0.25)', color: '#E0F4F7', border: '1px solid rgba(0,107,133,0.6)' }}
            >
              Contact Support
            </span>
          </a>
        )}
      </div>
    </div>
  );
}

function Stat({ icon: Icon, color, value, label }) {
  if (!value) return <div />;
  return (
    <div className="flex flex-col items-center text-center">
      <Icon className="w-6 h-6 mb-1" style={{ color }} />
      <p className="display font-extrabold text-xl sm:text-2xl text-white leading-tight">{value}</p>
      <p className="text-[10px] sm:text-xs uppercase tracking-wider text-white/60 mt-0.5">{label}</p>
    </div>
  );
}

function SocialRow({ def, url, brand }) {
  const Icon = def.icon;
  const href = socialHref(def.key, url);
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="flex items-center gap-3 rounded-2xl p-3 transition active:scale-[0.99] hover:brightness-110"
      style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}
      data-testid={`brand-profile-social-${def.key}`}
    >
      <span
        className="shrink-0 w-11 h-11 rounded-xl flex items-center justify-center"
        style={{ background: def.gradient, boxShadow: `0 4px 14px ${def.ctaColor}55` }}
      >
        {Icon
          ? <Icon className="w-5 h-5 text-white" />
          : <span className="text-white font-extrabold text-sm">♪</span>}
      </span>
      <div className="flex-1 min-w-0">
        <p className="font-bold text-white">{def.label}</p>
        <p className="text-xs text-white/60 truncate">{def.subLabel(url) || brand}</p>
      </div>
      <span
        className="shrink-0 display text-xs font-bold px-3 py-1.5 rounded-full"
        style={{ color: def.ctaColor, border: `1px solid ${def.border}`, background: 'rgba(0,0,0,0.25)' }}
      >
        {def.cta}
      </span>
      <ChevronRight className="w-4 h-4 text-white/50 shrink-0" />
    </a>
  );
}
